import React from 'react';

/**
 * Boundary para captura de errores de React
 *
 * Envuelve componentes hijos y muestra UI de fallback si ocurre un error.
 * En desarrollo muestra stack trace para debugging.
 *
 * @class
 * @extends React.Component
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  /**
   * Actualiza estado cuando ocurre un error en componentes hijos
   * @param {Error} error - Error capturado
   * @returns {{hasError: boolean}} Nuevo estado
   */
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h2>Oops! Algo salió mal</h2>
            <p>
              Ha ocurrido un error inesperado. Por favor, recarga la página o contacta al soporte técnico.
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Detalles del error (solo en desarrollo)</summary>
                <pre>{this.state.error && this.state.error.toString()}</pre>
                <hr />
                <pre>{this.state.errorInfo.componentStack}</pre>
              </details>
            )}
            
            <div className="error-actions">
              <button 
                className="btn btn-primary" 
                onClick={this.handleRetry}
              >
                <i className="fas fa-redo"></i>
                Intentar de nuevo
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-sync-alt"></i>
                Recargar página
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;